import type React from 'react'
import { createContext, useCallback, useMemo, useRef } from 'react'
import { IconSpriteSvg } from './icon-sprite-svg'
import type { IconTuple } from './icons'
import iconsMap from './icons'
import { randomId } from './random-id'
import { useIconMap } from './use-icon-map'

export type IconSpriteProviderProps = {
	icons?: IconTuple[]
	children: React.ReactNode
	size?: number // optional, falls Größe der Icons benötigt wird
	providerId?: string
}

export type SpriteContextType = {
	providerId: string
	size?: number // optional, falls Größe der Icons benötigt wird
	getHref: (name: string) => string | undefined
	getIcons: () => { name: string; icon: string }[]
}

export const SpriteContext = createContext<SpriteContextType | null>(null)

export function IconSpriteProvider({
	icons = iconsMap,
	children,
	size = 20,
	providerId: providerIdProp,
}: Readonly<IconSpriteProviderProps>) {
	const providerIdRef = useRef<string>(providerIdProp ?? randomId())
	const providerId = providerIdRef.current

	const { iconMap, hasIcon } = useIconMap(icons)

	const getHref = useCallback(
		(name: string): string | undefined =>
			hasIcon(name) ? `#${name}-${providerId}` : undefined,
		[hasIcon, providerId],
	)

	const getIcons = useCallback((): { name: string; icon: string }[] => {
		return Array.from(iconMap.keys()).map((name) => ({
			name,
			icon: `#${name}-${providerId}`,
		}))
	}, [iconMap, providerId])

	const value: SpriteContextType = useMemo(
		() => ({
			providerId,
			size,
			getHref,
			getIcons,
		}),
		[providerId, size, getHref, getIcons],
	)

	return (
		<SpriteContext.Provider value={value}>
			<IconSpriteSvg iconMap={iconMap} providerId={providerId} />
			{children}
		</SpriteContext.Provider>
	)
}
